import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle, useContext, useCallback } from "react";
import { BRAND_MAP_STYLE } from '../utils/mapStyle';
import {
  CustomButton6,
  CustomInputBox1,
  AddStopButton,
} from "./Templates.js";
import { FaLocationArrow, FaSearchLocation, FaLink, FaSpinner, FaExclamationTriangle, FaLock } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import { useToast } from './hooks/ToastContext';
import { AuthContext } from './Provider';
import "./GoogleMapsRoute.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const GoogleMapsRoute = forwardRef(({ onLocationSelect, disabled = false }, ref) => {
  const { user, profileLoading } = useContext(AuthContext);
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const mapRef = useRef(null);
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const stopRefs = useRef([]);
  const [map, setMap] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [fromValue, setFromValue] = useState("");
  const [toValue, setToValue] = useState("");
  const [waypoints, setWaypoints] = useState([]);
  const [waypointsPlaces, setWaypointsPlaces] = useState([]);
  const [distance, setDistance] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [userCountryData, setUserCountryData] = useState(null);

  // Function to get map configuration based on user's location
  const getUserMapConfig = useCallback(async () => {
    try {
      // Wait for profile to load before determining config
      if (profileLoading) {
        console.log('⏳ Profile still loading, waiting...');
        // Return null to indicate we're not ready yet
        return null;
      }

      // Check if user has country_code in profile
      let userCountry = user?.profile?.country_code;
      
      console.log('🌍 User country code from profile:', userCountry);

      // Default to Latvia if no user or country data
      const defaultConfig = {
        center: { lat: 56.9677, lng: 24.1056 }, // Latvia center
        zoom: 7,
        bounds: new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(55, 23),
          new window.google.maps.LatLng(58, 26)
        ),
        country: 'lv'
      };

      // If we have country_code, use it directly for map configuration
      if (userCountry) {
        console.log('✅ Using country_code for map config:', userCountry);
        
        // Map country codes to configurations
        const countryConfigs = {
          'NO': {
            center: { lat: 60.4720, lng: 8.4689 },
            zoom: 5,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(57.9, 4.6),
              new window.google.maps.LatLng(71.2, 31.3)
            ),
            country: 'no'
          },
          'SE': {
            center: { lat: 60.1282, lng: 18.6435 },
            zoom: 5,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(55.3, 11.0),
              new window.google.maps.LatLng(69.1, 24.2)
            ),
            country: 'se'
          },
          'LV': {
            center: { lat: 56.9677, lng: 24.1056 },
            zoom: 7,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(55, 23),
              new window.google.maps.LatLng(58, 26)
            ),
            country: 'lv'
          },
          'LT': {
            center: { lat: 55.1694, lng: 23.8813 },
            zoom: 7,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(53.9, 20.9),
              new window.google.maps.LatLng(56.5, 26.8)
            ),
            country: 'lt'
          },
          'EE': {
            center: { lat: 58.5953, lng: 25.0136 },
            zoom: 7,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(57.5, 21.5),
              new window.google.maps.LatLng(59.7, 28.2)
            ),
            country: 'ee'
          },
          'DK': {
            center: { lat: 56.2639, lng: 9.5018 },
            zoom: 7,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(54.5, 8.0),
              new window.google.maps.LatLng(57.8, 15.2)
            ),
            country: 'dk'
          },
          'PL': {
            center: { lat: 51.9194, lng: 19.1451 },
            zoom: 6,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(49.0, 14.1),
              new window.google.maps.LatLng(54.8, 24.1)
            ),
            country: 'pl'
          },
          'DE': {
            center: { lat: 51.1657, lng: 10.4515 },
            zoom: 6,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(47.3, 5.9),
              new window.google.maps.LatLng(55.1, 15.0)
            ),
            country: 'de'
          },
          'FR': {
            center: { lat: 46.6034, lng: 1.8883 },
            zoom: 6,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(41.3, -5.1),
              new window.google.maps.LatLng(51.1, 9.6)
            ),
            country: 'fr'
          },
          'GB': {
            center: { lat: 55.3781, lng: -3.4360 },
            zoom: 6,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(49.9, -8.6),
              new window.google.maps.LatLng(60.9, 1.8)
            ),
            country: 'gb'
          }
        };
        
        const config = countryConfigs[userCountry.toUpperCase()];
        if (config) {
          console.log(`🗺️ Map configured for ${userCountry.toUpperCase()}`);
          setUserCountryData(config);
          return config;
        }
      }

      // Fallback: try to use city coordinates if available
      if (!user || !user.profile?.city || !user.profile?.city_lat || !user.profile?.city_lng) {
        console.log('No user location data, using default (Latvia)');
        return defaultConfig;
      }

      const userLat = parseFloat(user.profile.city_lat);
      const userLng = parseFloat(user.profile.city_lng);
      const userCity = user.profile.city;
      
      console.log('User location from profile:', userCity, userLat, userLng);

      // Determine country and map bounds based on coordinates
      let mapConfig = defaultConfig;
      
      // Check coordinates to determine country and set appropriate bounds
      if (userLng >= 20 && userLng <= 28 && userLat >= 55 && userLat <= 58) {
        // Latvia/Lithuania/Estonia region
        if (userCity?.toLowerCase().includes('lithu') || 
            (userLng >= 21 && userLng <= 26.8 && userLat >= 53.9 && userLat <= 56.5)) {
          // Lithuania
          mapConfig = {
            center: { lat: 55.1694, lng: 23.8813 },
            zoom: 7,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(53.9, 20.9),
              new window.google.maps.LatLng(56.5, 26.8)
            ),
            country: 'lt'
          };
          console.log('🇱🇹 User location detected as Lithuania');
        } else if (userCity?.toLowerCase().includes('eston') || 
                   (userLng >= 21.5 && userLng <= 28.2 && userLat >= 57.5 && userLat <= 59.7)) {
          // Estonia  
          mapConfig = {
            center: { lat: 58.5953, lng: 25.0136 },
            zoom: 7,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(57.5, 21.5),
              new window.google.maps.LatLng(59.7, 28.2)
            ),
            country: 'ee'
          };
          console.log('🇪🇪 User location detected as Estonia');
        } else {
          // Default to Latvia for this region
          console.log('🇱🇻 User location detected as Latvia (default for region)');
        }
      } else if (userCity?.toLowerCase().includes('sweden') || userCity?.toLowerCase().includes('stockholm') ||
                 userCity?.toLowerCase().includes('göteborg') || userCity?.toLowerCase().includes('malmö') ||
                 (userLng >= 11 && userLng <= 24 && userLat >= 55 && userLat <= 69)) {
        // Sweden (check city names first, then coordinates)
        mapConfig = {
          center: { lat: 60.1282, lng: 18.6435 },
          zoom: 5,
          bounds: new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(55.3, 11.0),
            new window.google.maps.LatLng(69.1, 24.2)
          ),
          country: 'se'
        };
        console.log('🇸🇪 User location detected as Sweden via city name or coordinates');
      } else if (userLng >= 10 && userLng <= 25 && userLat >= 53 && userLat <= 70) {
        // Northern/Eastern Europe (remaining countries)
        if (userLng >= 10 && userLng <= 24 && userLat >= 63 && userLat <= 71) {
          // Norway
          mapConfig = {
            center: { lat: 60.4720, lng: 8.4689 },
            zoom: 5,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(57.9, 4.6),
              new window.google.maps.LatLng(71.2, 31.3)
            ),
            country: 'no'
          };
          console.log('🇳🇴 User location detected as Norway');
        } else if (userLng >= 12 && userLng <= 15 && userLat >= 54 && userLat <= 58) {
          // Denmark
          mapConfig = {
            center: { lat: 56.2639, lng: 9.5018 },
            zoom: 7,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(54.5, 8.0),
              new window.google.maps.LatLng(57.8, 15.2)
            ),
            country: 'dk'
          };
          console.log('🇩🇰 User location detected as Denmark');
        } else if (userLng >= 5 && userLng <= 15 && userLat >= 47 && userLat <= 55) {
          // Germany  
          mapConfig = {
            center: { lat: 51.1657, lng: 10.4515 },
            zoom: 6,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(47.3, 5.9),
              new window.google.maps.LatLng(55.1, 15.0)
            ),
            country: 'de'
          };
          console.log('🇩🇪 User location detected as Germany');
        } else if (userLng >= 14 && userLng <= 24 && userLat >= 49 && userLat <= 55) {
          // Poland
          mapConfig = {
            center: { lat: 51.9194, lng: 19.1451 },
            zoom: 6,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(49.0, 14.1),
              new window.google.maps.LatLng(54.8, 24.1)
            ),
            country: 'pl'
          };
          console.log('🇵🇱 User location detected as Poland');
        }
      } else if (userLng >= -10 && userLng <= 10 && userLat >= 36 && userLat <= 60) {
        // Western Europe
        if (userLng >= -5 && userLng <= 8 && userLat >= 42 && userLat <= 51) {
          // France
          mapConfig = {
            center: { lat: 46.6034, lng: 1.8883 },
            zoom: 6,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(41.3, -5.1),
              new window.google.maps.LatLng(51.1, 9.6)
            ),
            country: 'fr'
          };
          console.log('🇫🇷 User location detected as France');
        } else if (userLng >= -9 && userLng <= 2 && userLat >= 50 && userLat <= 59) {
          // United Kingdom
          mapConfig = {
            center: { lat: 55.3781, lng: -3.4360 },
            zoom: 6,
            bounds: new window.google.maps.LatLngBounds(
              new window.google.maps.LatLng(49.9, -8.6),
              new window.google.maps.LatLng(60.9, 1.8)
            ),
            country: 'gb'
          };
          console.log('🇬🇧 User location detected as United Kingdom');
        }
      } else {
        // For locations outside Europe, use a broader view centered on user location
        mapConfig = {
          center: { lat: userLat, lng: userLng },
          zoom: 6,
          bounds: new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(userLat - 3, userLng - 5),
            new window.google.maps.LatLng(userLat + 3, userLng + 5)
          ),
          country: null // No country restriction for international users
        };
        console.log('🌍 User location detected as international:', userCity);
      }

      setUserCountryData(mapConfig);
      return mapConfig;
    } catch (error) {
      console.error('Error determining user map config:', error);
      // Return default Latvia config on error
      return {
        center: { lat: 56.9677, lng: 24.1056 },
        zoom: 7,
        bounds: new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(55, 23),
          new window.google.maps.LatLng(58, 26)
        ),
        country: 'lv'
      };
    }
  }, [user, profileLoading]);

  // First, check if Google Maps API is available
  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setIsMapLoading(false);
      } else {
        console.warn("Google Maps API not loaded yet, waiting...");
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  // Initialize the map after the component has fully mounted and API is loaded
  useEffect(() => {
    if (isMapLoading || mapInitialized || !mapRef.current || disabled || profileLoading) return;

    // Add a small delay to ensure DOM is fully rendered
    const initTimer = setTimeout(async () => {
      try {
        console.log("Initializing Google Maps...");
        console.log("Map element exists:", !!mapRef.current);
        console.log("Map element dimensions:", mapRef.current.offsetWidth, "x", mapRef.current.offsetHeight);

        // Get user-specific map configuration
        const mapConfig = await getUserMapConfig();
        console.log('🗺️ Map config for user:', mapConfig);

        // Initialize map with user's location as default
        const newMap = new window.google.maps.Map(mapRef.current, {
          center: mapConfig.center,
          zoom: mapConfig.zoom,
          fullscreenControl: false, // Disable fullscreen control to prevent layout issues
          mapTypeControl: true,
          streetViewControl: false, // Disable street view to prevent layout issues
          styles: BRAND_MAP_STYLE,
        });

        // Set map bounds based on user's country/region
        if (mapConfig.bounds) {
          newMap.fitBounds(mapConfig.bounds);
        }

        const directionsService = new window.google.maps.DirectionsService();
        const directionsRenderer = new window.google.maps.DirectionsRenderer({
          map: newMap,
          suppressMarkers: true,
        });

        setMap(newMap);
        setDirectionsService(directionsService);
        setDirectionsRenderer(directionsRenderer);
        setMapInitialized(true);
        console.log("Map initialized successfully with user config");
      } catch (e) {
        console.error("Error initializing map:", e);
        setError(t('errors.map_initialization_failed') || "Failed to initialize map");
      }
    }, 100); // Short delay to ensure DOM is ready
    
    return () => clearTimeout(initTimer);
  }, [isMapLoading, mapInitialized, t, disabled, getUserMapConfig, profileLoading]);

  // Helper function to parse address string into components
  const parseAddressString = (addressString) => {
    if (!addressString) return [];
    
    console.log('Parsing address string:', addressString);
    
    // This is a simple parser - in production you might want something more sophisticated
    const parts = addressString.split(',').map(p => p.trim());
    const components = [];
    
    // Try to identify different parts
    parts.forEach((part, index) => {
      if (index === 0) {
        // First part might be street number + street name or just street name
        components.push({
          long_name: part,
          short_name: part,
          types: ['street_address', 'route']
        });
      } else {
        // Look for city-like names (not containing codes like LV-XXXX)
        if (!part.includes('LV-') && !part.toLowerCase().includes('latvia')) {
          components.push({
            long_name: part,
            short_name: part,
            types: ['locality', 'political']
          });
        } else if (part.toLowerCase().includes('latvia') || part === 'LV') {
          // This is the country
          components.push({
            long_name: 'Latvia',
            short_name: 'LV',
            types: ['country', 'political']
          });
        } else {
          // Administrative areas, postal codes, etc.
          components.push({
            long_name: part,
            short_name: part,
            types: ['administrative_area_level_2', 'political']
          });
        }
      }
    });
    
    console.log('Parsed address components:', components);
    return components;
  };

  // Function to simplify address format
  const simplifyAddress = (place) => {
    if (!place || !place.address_components) {
      return place?.formatted_address || '';
    }

    // Extract key components
    const locality = place.address_components.find(c => 
      c.types.includes('locality'))?.long_name || '';
    
    const route = place.address_components.find(c => 
      c.types.includes('route'))?.long_name || '';
      
    const streetNumber = place.address_components.find(c => 
      c.types.includes('street_number'))?.long_name || '';
      
    const country = place.address_components.find(c => 
      c.types.includes('country'))?.short_name || '';
    
    // Construct a simplified address
    let simplifiedAddress = '';
    
    // Add street address if available
    if (streetNumber && route) {
      simplifiedAddress = `${streetNumber} ${route}, `;
    } else if (route) {
      simplifiedAddress = `${route}, `;
    }
    
    // Add city
    if (locality) {
      simplifiedAddress += locality;
    }
    
    // Add country if different from Latvia
    if (country && country !== 'LV') {
      simplifiedAddress += `, ${country}`;
    }
    
    return simplifiedAddress || place.formatted_address || '';
  };

  // Setup autocomplete after map is initialized
  useEffect(() => {
    if (!mapInitialized || !map || disabled) return;

    try {
      console.log("Setting up autocomplete...");
      
      // Use user-specific bounds and country restrictions if available
      const autocompleteOptions = {
        bounds: userCountryData?.bounds || new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(55, 23),
          new window.google.maps.LatLng(58, 26)
        ),
        strictBounds: false,
        // Use user's country if available, otherwise default to Latvia
        ...(userCountryData?.country && { componentRestrictions: { country: userCountryData.country } })
      };
      
      console.log('🌍 Autocomplete config:', autocompleteOptions);

      // Setup autocomplete for 'from' input
      if (fromInputRef.current) {
        const fromAutoComplete = new window.google.maps.places.Autocomplete(
          fromInputRef.current.querySelector("input"),
          autocompleteOptions
        );

        fromAutoComplete.addListener("place_changed", () => {
          const place = fromAutoComplete.getPlace();
          if (place.geometry) {
            const simplifiedAddr = simplifyAddress(place);
            setFromValue(simplifiedAddr);
            // Pass the entire place object for detailed data
            // but display the simplified address to the user
            onLocationSelect("address_from", place); 
          }
        });
      }

      // Setup autocomplete for 'to' input
      if (toInputRef.current) {
        const toAutoComplete = new window.google.maps.places.Autocomplete(
          toInputRef.current.querySelector("input"),
          autocompleteOptions
        );

        toAutoComplete.addListener("place_changed", () => {
          const place = toAutoComplete.getPlace();
          if (place.geometry) {
            const simplifiedAddr = simplifyAddress(place);
            setToValue(simplifiedAddr);
            onLocationSelect("address_to", place);
          }
        });
      }

      // Setup autocomplete for waypoints
      stopRefs.current.forEach((ref, index) => {
        if (ref && !ref.hasAutocomplete) {
          const stopAutoComplete = new window.google.maps.places.Autocomplete(
            ref.querySelector("input"),
            autocompleteOptions
          );

          stopAutoComplete.addListener("place_changed", () => {
            const place = stopAutoComplete.getPlace();
            if (place.geometry) {
              const simplifiedAddr = simplifyAddress(place);
              
              // Update the displayed waypoint text
              const updatedWaypoints = [...waypoints];
              updatedWaypoints[index] = simplifiedAddr;
              setWaypoints(updatedWaypoints);
              
              // Store the full place object for accurate directions
              const updatedPlaces = [...waypointsPlaces];
              updatedPlaces[index] = place;
              setWaypointsPlaces(updatedPlaces);
              
              onLocationSelect(`waypoint_${index}`, place);
            }
          });

          ref.hasAutocomplete = true;
        }
      });
      
      console.log("Autocomplete setup complete");
    } catch (e) {
      console.error("Error setting up autocomplete:", e);
      setError(t('errors.autocomplete_setup_failed') || "Failed to set up address search");
    }
  }, [mapInitialized, map, waypoints, waypointsPlaces, t, onLocationSelect, disabled, userCountryData]);

  // Cleanup function
  useEffect(() => {
    return () => {
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
      }
    };
  }, [directionsRenderer]);

  const generateStaticMapUrl = (directions) => {
    if (!directions || !directions.routes || directions.routes.length === 0) {
      console.error("Invalid directions object");
      return null;
    }
    
    try {
      const path = directions.routes[0].overview_polyline;
      const start = directions.routes[0].legs[0].start_location;
      const end = directions.routes[0].legs[directions.routes[0].legs.length - 1].end_location;
      const encodedPath = encodeURIComponent(path);

      return `https://maps.googleapis.com/maps/api/staticmap?size=600x300&path=enc:${encodedPath}&markers=color:green%7Clabel:S%7C${start.lat()},${start.lng()}&markers=color:red%7Clabel:E%7C${end.lat()},${end.lng()}&key=${GOOGLE_MAPS_API_KEY}`;
    } catch (e) {
      console.error("Error generating static map URL:", e);
      return null;
    }
  };

  // Extract route polyline for route matching
  const extractRoutePolyline = (directions) => {
    if (!directions || !directions.routes || directions.routes.length === 0) {
      console.error("Invalid directions object for polyline extraction");
      return null;
    }
    
    try {
      // Log the structure to debug
      console.log("Route object structure for debugging:", JSON.stringify(directions, null, 2));
      
      // First attempt: standard Google Maps format
      if (directions.routes[0].overview_polyline && 
          directions.routes[0].overview_polyline.points) {
        console.log("Found standard overview_polyline.points format");
        return directions.routes[0].overview_polyline.points;
      }
      
      // Second attempt: check if overview_polyline is directly a string
      if (directions.routes[0].overview_polyline && 
          typeof directions.routes[0].overview_polyline === 'string') {
        console.log("Found overview_polyline as string");
        return directions.routes[0].overview_polyline;
      }
      
      // Third attempt: create a polyline from the path coordinates
      console.log("No points in overview_polyline, trying to create from path");
      if (directions.routes[0].legs && directions.routes[0].legs.length > 0) {
        console.log("Creating fallback polyline from route legs");
        
        // Create a fallback polyline by collecting all path points
        const points = [];
        
        // Add the starting point of the first leg
        const firstLeg = directions.routes[0].legs[0];
        if (firstLeg.start_location) {
          points.push([
            firstLeg.start_location.lat(),
            firstLeg.start_location.lng()
          ]);
        }
        
        // Process all legs
        directions.routes[0].legs.forEach(leg => {
          // Add all steps within each leg
          if (leg.steps && leg.steps.length > 0) {
            leg.steps.forEach(step => {
              if (step.path && step.path.length > 0) {
                // Add path points from each step
                step.path.forEach(pathPoint => {
                  points.push([
                    pathPoint.lat(),
                    pathPoint.lng()
                  ]);
                });
              } else if (step.end_location) {
                // If no path, at least add the end location
                points.push([
                  step.end_location.lat(),
                  step.end_location.lng()
                ]);
              }
            });
          }
          
          // Always include the end point of each leg
          if (leg.end_location) {
            points.push([
              leg.end_location.lat(),
              leg.end_location.lng()
            ]);
          }
        });
        
        // Create a simple polyline format: lat1,lng1|lat2,lng2|...
        if (points.length > 1) {
          const fallbackPolyline = points.map(p => {
            return `${p[0].toFixed(6)},${p[1].toFixed(6)}`;
          }).join('|');
          
          console.log("Created fallback polyline with", points.length, "points");
          return fallbackPolyline;
        }
      }
      
      // Final attempt: Create minimal polyline with just start and end
      if (directions.routes[0].legs && directions.routes[0].legs.length > 0) {
        const firstLeg = directions.routes[0].legs[0];
        const lastLeg = directions.routes[0].legs[directions.routes[0].legs.length - 1];
        
        if (firstLeg.start_location && lastLeg.end_location) {
          const minimalPolyline = `${firstLeg.start_location.lat().toFixed(6)},${firstLeg.start_location.lng().toFixed(6)}|${lastLeg.end_location.lat().toFixed(6)},${lastLeg.end_location.lng().toFixed(6)}`;
          console.log("Created minimal fallback polyline with start and end points");
          return minimalPolyline;
        }
      }
      
      console.error("Could not extract or create a polyline from the response");
      return null;
    } catch (e) {
      console.error("Error extracting route polyline:", e);
      
      // Try one more fallback approach if we have bounds
      try {
        if (directions.routes[0].bounds) {
          const bounds = directions.routes[0].bounds;
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          
          // Create a bounding box polyline (4 points forming a rectangle)
          const boxPolyline = `${ne.lat().toFixed(6)},${ne.lng().toFixed(6)}|${ne.lat().toFixed(6)},${sw.lng().toFixed(6)}|${sw.lat().toFixed(6)},${sw.lng().toFixed(6)}|${sw.lat().toFixed(6)},${ne.lng().toFixed(6)}|${ne.lat().toFixed(6)},${ne.lng().toFixed(6)}`;
          console.log("Created bounding box polyline as last resort");
          return boxPolyline;
        }
      } catch (boundError) {
        console.error("Error creating bounds fallback:", boundError);
      }
      
      return null;
    }
  };

  const handleRouteCalculation = (response) => {
    if (response && response.routes && response.routes[0]) {
      const route = response.routes[0];
      const distanceInKm = route.legs.reduce((total, leg) => total + leg.distance.value, 0) / 1000;
      const roundedDistance = Number(distanceInKm.toFixed(2));
      onLocationSelect("distance_km", roundedDistance);
      
      // IMPORTANT FIX: Extract actual final destination from route response
      try {
        const lastLeg = route.legs[route.legs.length - 1]; // Get the final leg of the journey
        if (lastLeg && lastLeg.end_location && lastLeg.end_address) {
          console.log('Actual final destination from route:', lastLeg.end_address);
          console.log('Final destination coordinates:', lastLeg.end_location.lat(), lastLeg.end_location.lng());
          
          // Create a place-like object for the actual final destination
          const actualDestination = {
            formatted_address: lastLeg.end_address,
            geometry: {
              location: {
                lat: () => lastLeg.end_location.lat(),
                lng: () => lastLeg.end_location.lng()
              }
            },
            // Parse address components from the end_address
            address_components: parseAddressString(lastLeg.end_address)
          };
          
          console.log('Created actual destination object:', actualDestination);
          
          // Update the final destination address with the ACTUAL final destination
          onLocationSelect("address_to", actualDestination);
          
          // Also update the display value to match the actual destination
          const simplifiedEndAddr = simplifyAddress(actualDestination);
          setToValue(simplifiedEndAddr);
          
          console.log('✅ FIXED: Updated final destination from', toValue, 'to actual route endpoint:', simplifiedEndAddr);
          console.log('Final destination will be saved as:', {
            street: actualDestination.formatted_address,
            city: actualDestination.address_components.find(c => c.types.includes('locality'))?.long_name,
            country: actualDestination.address_components.find(c => c.types.includes('country'))?.long_name
          });
        } else {
          console.warn('Could not extract final destination - missing end_location or end_address');
        }
      } catch (addressError) {
        console.error('Error extracting final destination address:', addressError);
      }
      
      return roundedDistance;
    }
    return null;
  };

  // Check if inputs are valid and show appropriate messages
  const validateInputs = () => {
    if (!fromValue || !toValue) {
      const errorMsg = t('errors.address_required') || "Please provide both 'From' and 'To' addresses";
      setError(errorMsg);
      addToast({
        type: 'error',
        title: t('errors.missing_addresses'),
        description: errorMsg
      });
      return false;
    }
    
    // Check if waypoints are valid
    const hasInvalidWaypoint = waypoints.some((waypoint, index) => 
      waypoint && (!waypointsPlaces[index] || !waypointsPlaces[index].geometry)
    );
    
    if (hasInvalidWaypoint) {
      const errorMsg = t('errors.invalid_waypoint') || "One or more stops have invalid addresses";
      setError(errorMsg);
      addToast({
        type: 'error',
        title: t('errors.validation_error'),
        description: errorMsg
      });
      return false;
    }
    
    return true;
  };

  const calculateAndDisplayRoute = async () => {
    // Don't proceed if component is disabled
    if (disabled) {
      return Promise.reject(new Error("Form is disabled"));
    }
    
    return new Promise((resolve, reject) => {
      setIsCalculating(true);
      setError(null);
      
      try {
        // First validate the inputs
        if (!validateInputs()) {
          setIsCalculating(false);
          reject(new Error(t('errors.address_required')));
          return;
        }
        
        if (!directionsService || !directionsRenderer) {
          const errorMsg = t('errors.maps_service_not_available') || "Maps service not available";
          setError(errorMsg);
          addToast({
            type: 'error',
            title: t('errors.service_unavailable'),
            description: errorMsg
          });
          setIsCalculating(false);
          reject(new Error(errorMsg));
          return;
        }

        // Prepare waypoints - only include valid ones
        const validWaypoints = waypointsPlaces
          .filter(place => place && place.geometry)
          .map(place => ({
            location: new window.google.maps.LatLng(
              place.geometry.location.lat(),
              place.geometry.location.lng()
            ),
            stopover: true
          }));

        // Use setTimeout to avoid immediate execution that might cause UI blocking
        setTimeout(() => {
          directionsService.route(
            {
              origin: fromValue,
              destination: toValue,
              waypoints: validWaypoints,
              travelMode: window.google.maps.TravelMode.DRIVING,
              optimizeWaypoints: true // Help avoid NOT_FOUND errors
            },
            (response, status) => {
              if (status === "OK") {
                directionsRenderer.setDirections(response);
                const staticMapUrl = generateStaticMapUrl(response);
                const distanceKm = handleRouteCalculation(response);
                setDistance(distanceKm);
                
                if (staticMapUrl) {
                  onLocationSelect("static_map_url", staticMapUrl);
                }
                
                // Extract and pass the route polyline
                const routePolyline = extractRoutePolyline(response);
                if (routePolyline) {
                  onLocationSelect("route_polyline", routePolyline);
                  console.log("Route polyline captured:", routePolyline.substring(0, 30) + "...");
                  console.log("Route polyline length:", routePolyline.length);
                  
                  // Update the route polyline state for the validate function
                  // This ensures that validation will pass since we have a polyline
                  console.log("Route polyline saved to form state");
                  
                  // Success notification after successful polyline extraction
                  addToast({
                    type: 'success',
                    title: t('forms.route_calculated'),
                    description: t('forms.distance_calculated', { 
                      distance: distanceKm,
                      from: fromValue,
                      to: toValue
                    })
                  });
                  
                  console.log('Route calculation completed successfully with corrected endpoints');
                } else {
                  console.error("Failed to extract route polyline from response");
                  
                  // Try a manual implementation as fallback
                  try {
                    if (response && response.routes && response.routes[0] && 
                        response.routes[0].legs) {
                      console.log("Attempting alternate polyline extraction method...");
                      // Create a simple representation of the route as a polyline
                      const legs = response.routes[0].legs;
                      let points = [];
                      
                      // Add all important points
                      for (const leg of legs) {
                        // Add start point
                        if (leg.start_location) {
                          points.push([leg.start_location.lat(), leg.start_location.lng()]);
                        }
                        
                        // Add step points if available
                        if (leg.steps && leg.steps.length > 0) {
                          for (const step of leg.steps) {
                            if (step.end_location) {
                              points.push([step.end_location.lat(), step.end_location.lng()]);
                            }
                          }
                        }
                        
                        // Add end point
                        if (leg.end_location) {
                          points.push([leg.end_location.lat(), leg.end_location.lng()]);
                        }
                      }
                      
                      if (points.length > 0) {
                        // Convert to a simple "lat,lng|lat,lng" format
                        const simplePolyline = points.map(p => {
                          return `${p[0].toFixed(6)},${p[1].toFixed(6)}`;
                        }).join('|');
                        
                        console.log("Created fallback polyline:", simplePolyline.substring(0, 30) + "...");
                        console.log("Fallback polyline points:", points.length);
                        
                        onLocationSelect("route_polyline", simplePolyline);
                        
                        // Route state was saved, so consider it a success
                        addToast({
                          type: 'success',
                          title: t('forms.route_calculated'),
                          description: t('forms.distance_calculated', { 
                            distance: distanceKm,
                            from: fromValue,
                            to: toValue
                          })
                        });
                        
                        console.log('Route calculation completed with fallback polyline and corrected endpoints');
                      } else {
                        console.error("Fallback polyline extraction failed - no points");
                        addToast({
                          type: 'warning',
                          title: t('forms.route_calculated'),
                          description: t('errors.route_data_incomplete')
                        });
                      }
                    }
                  } catch (polylineError) {
                    console.error("Error in fallback polyline extraction:", polylineError);
                    addToast({
                      type: 'warning',
                      title: t('forms.route_calculated'),
                      description: t('errors.route_data_incomplete')
                    });
                  }
                }
                
                onLocationSelect("distance_km", distanceKm);
                setIsCalculating(false);
                resolve(distanceKm);
              } else {
                console.error("Directions request failed due to " + status);
                let errorMsg;
                
                // Customize error message based on status
                switch (status) {
                  case "NOT_FOUND":
                    errorMsg = t('errors.location_not_found') || "One or more locations couldn't be found. Please check your addresses.";
                    break;
                  case "ZERO_RESULTS":
                    errorMsg = t('errors.no_route') || "No route could be found between these locations.";
                    break;
                  default:
                    errorMsg = t('errors.route_calculation_failed') || "Failed to calculate route";
                }
                
                setError(errorMsg);
                
                addToast({
                  type: 'error',
                  title: t('errors.route_error'),
                  description: errorMsg
                });
                
                setIsCalculating(false);
                reject(new Error(errorMsg));
              }
            }
          );
        }, 100);
      } catch (error) {
        console.error("Error in route calculation:", error);
        const errorMsg = t('errors.route_calculation_failed') || "Failed to calculate route";
        setError(errorMsg);
        
        addToast({
          type: 'error',
          title: t('errors.route_error'),
          description: errorMsg
        });
        
        setIsCalculating(false);
        reject(error);
      }
    });
  };

  // Expose the function to parent component
  useImperativeHandle(ref, () => ({
    calculateAndDisplayRoute: () => {
      if (disabled) {
        return Promise.resolve(null);
      }
      
      try {
        return calculateAndDisplayRoute();
      } catch (error) {
        // This catches any errors and prevents them from propagating
        console.error("Error calculating route:", error);
        addToast({
          type: 'error',
          title: t('errors.route_error'),
          description: error.message || t('errors.route_calculation_failed')
        });
        
        // Return a resolved promise with null to prevent uncaught errors
        return Promise.resolve(null);
      }
    }
  }));

  const handleWaypointChange = (index, value) => {
    if (disabled) return;
    
    const updatedWaypoints = [...waypoints];
    updatedWaypoints[index] = value;
    setWaypoints(updatedWaypoints);
    
    // Clear the place object when the user is typing manually
    const updatedPlaces = [...waypointsPlaces];
    updatedPlaces[index] = null;
    setWaypointsPlaces(updatedPlaces);
    
    onLocationSelect(`waypoint_${index}`, value);
  };

  const addWaypoint = () => {
    if (disabled || waypoints.length >= 3) return;
    
    setWaypoints([...waypoints, ""]);
    setWaypointsPlaces([...waypointsPlaces, null]);
  };

  const removeWaypoint = (index) => {
    if (disabled) return;
    
    const updatedWaypoints = waypoints.filter((_, i) => i !== index);
    setWaypoints(updatedWaypoints);
    
    const updatedPlaces = waypointsPlaces.filter((_, i) => i !== index);
    setWaypointsPlaces(updatedPlaces);
  };
  
  // Handler for show route button
  const handleShowRoute = (e) => {
    e.preventDefault();
    
    if (disabled) return;
    
    try {
      calculateAndDisplayRoute().catch(err => {
        // Error is already handled inside calculateAndDisplayRoute
        console.log("Route calculation failed:", err.message);
      });
    } catch (error) {
      // This is a fallback in case of unexpected errors
      console.error("Unexpected error in handleShowRoute:", error);
      addToast({
        type: 'error',
        title: t('errors.unexpected_error'),
        description: t('errors.please_try_again')
      });
    }
  };

  if (isMapLoading || profileLoading) {
    return (
      <div className="map-loading">
        <FaSpinner className="spinner" />
        <p>{profileLoading ? (t('app.loading_profile') || "Loading profile...") : (t('app.loading') || "Loading...")}</p>
      </div>
    );
  }

  // If the form is disabled, show an overlay with a disabled state
  if (disabled) {
    return (
      <div className="travel-route-container disabled">
        <div className="map-disabled-overlay">
          <FaLock className="disabled-icon" />
          <p>Map disabled - monthly post limit reached</p>
        </div>
        
        <div className="travel-route-inputs disabled">
          <div className="input-group from-input" ref={fromInputRef}>
            <div className="input-icon">
              <FaLocationArrow />
            </div>
            <CustomInputBox1
              placeholder={t('forms.traveling_from') || "Traveling from"}
              value={fromValue}
              onChange={(e) => {}}
              disabled={true}
            />
          </div>

          <div className="input-group to-input" ref={toInputRef}>
            <div className="input-icon">
              <FaSearchLocation />
            </div>
            <CustomInputBox1
              placeholder={t('forms.traveling_to') || "Traveling to"}
              value={toValue}
              onChange={(e) => {}}
              disabled={true}
            />
          </div>

          <div className="show-route-container">
            <CustomButton6 
              onClick={(e) => e.preventDefault()} 
              label={t('forms.show_route') || "Show Route"}
              disabled={true}
            />
          </div>
        </div>

        <div 
          className="map-box disabled"
          ref={mapRef}
        />
      </div>
    );
  }

  return (
    <div className="travel-route-container">
      {error && (
        <div className="route-error-message">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}
      <div className="travel-route-inputs">
        <div className="input-group from-input" ref={fromInputRef}>
          <div className="input-icon">
          </div>
          <CustomInputBox1
            placeholder={t('forms.traveling_from') || "Traveling from"}
            value={fromValue}
            onChange={(e) => setFromValue(e.target.value)}
          />
        </div>

        {waypoints.map((waypoint, index) => (
          <div
            key={index}
            className="waypoint-input-group"
          >
            <div
              ref={(el) => (stopRefs.current[index] = el)}
              className="input-group"
            >
              <div className="input-icon">
              </div>
              <CustomInputBox1
                placeholder={`${t('forms.stop') || "Stop"} ${index + 1}`}
                value={waypoint}
                onChange={(e) => handleWaypointChange(index, e.target.value)}
                >
                  <FaLink />
              </CustomInputBox1>
              
            </div>
            <button 
              className="G-remove-waypoint-btn"
              onClick={() => removeWaypoint(index)}
            >
              ✕
            </button>
          </div>
        ))}

        <div className="add-stop-container">
          <AddStopButton
            onClick={addWaypoint}
            disabled={waypoints.length >= 3 || disabled}
            text={t('buttons.add')}
          >
            <FaLink />
          </AddStopButton>
        </div>

        <div className="input-group to-input" ref={toInputRef}>
          <div className="input-icon">
          </div>
          <CustomInputBox1
            placeholder={t('forms.traveling_to') || "Traveling to"}
            value={toValue}
            onChange={(e) => setToValue(e.target.value)}
          />
        </div>

        <div className="show-route-container">
          <CustomButton6 
            onClick={handleShowRoute} 
            label={isCalculating ? 
              (t('app.calculating') || "Calculating...") : 
              (t('forms.show_route') || "Show Route")
            }
            disabled={isCalculating}
          />
          {isCalculating && <FaSpinner className="spinner" />}
          {distance && (
            <div className="distance-info">
              <span>{t('forms.distance') || "Distance"}: </span>
              <strong>{distance} km</strong>
            </div>
          )}
        </div>
      </div>

      <div 
        className="map-box"
        ref={mapRef}
      />
      </div>
  );
});

export default GoogleMapsRoute;